import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import dayjs from 'dayjs';

import config from '../config';

import {
  FormItem,
  InquiryForm,
  Smtp,
  UserMailTemplate,
} from '../models/Inquiry/Inquiry';

interface Token {
  iss?: string
  iat: number
  exp: number
}

const isTokenValid = (token?: string | null): boolean => {
  if (!token) return false;
  try {
    const jwt: Token = jwtDecode(token);

    if (!jwt?.exp) return false;
    const expire = dayjs.unix(jwt.exp);

    return dayjs().isBefore(expire);
  } catch (error) {
    return false;
  }
};

const fetchNewToken = async (): Promise<string> => {
  const apiKey = window.localStorage.getItem(config.api.webdbKeyStorage);

  if (!apiKey) return '';

  const res = await axios.post(
    `${config.api.webdbUrl}/tokens`,
    {},
    {
      headers: {
        Authorization: `x-api-key ${apiKey}`,
      },
    },
  );

  window.localStorage.setItem(config.auth.webdbStorage, res?.data?.token);

  return res?.data?.token;
};

const api = axios.create({
  baseURL: config.api.webdbUrl,
});

api.interceptors.request.use(async (axiosConfig) => {
  // Get JWT token from local storage
  let token = window.localStorage.getItem(config.auth.webdbStorage);

  // Check if token is still valid
  // If still valid, use it
  // If not, refresh it using /tokens endpoint (with api key)
  if (!isTokenValid(token)) {
    try {
      token = await fetchNewToken();
    } catch (error) {
      console.error('error fetching webdb token', error);
    }
  }

  if (token) {
    axiosConfig.headers.authorization = `Bearer ${token}`;
  }

  return axiosConfig;
});

export const getForms = async (limit = 15, page = 1) => api.get<{
  data: InquiryForm[];
  metadata: {
    page: number;
    limit: number;
    page_count: number;
    total_count: number;
  };
}>(`/simple_forms?form_type=1&limit=${limit}&page=${page}`);
export const getSmtps = async () => api.get<{ data: Smtp[] }>('/user_smtp?limit=9999&page=1');
export const getMailTemplates = async () => api.get<{ data: UserMailTemplate[] }>('/user_templates?limit=9999&page=1');
export const getForm = async (id: number) => api.get<InquiryForm>(`/forms/${id}`);
export const getFormItems = async () => api.get<{ data: FormItem[] }>('/form_items?type=1');
export const postForm = async (payload: InquiryForm) => api.post('/forms', payload);
export const deleteForm = async (id: number) => api.delete(`/forms/${id}`);
